import { Link } from "react-router-dom";
import { useSeo, SITE_URL } from "@/lib/seo";

/**
 * NotFound — SPA catch-all for unknown URLs. We can't return a real HTTP
 * 404 from a static-hosted SPA, but we can (a) set meta robots=noindex so
 * Google does not index infinite variants and (b) point canonical at "/"
 * so any accidental link equity flows to the homepage. Static hosts that
 * support it will still serve build_static/404.html with a real 404 code
 * for direct hits — that file exists in parallel.
 */
export const NotFound = () => {
  useSeo({
    title: "Page not found · Asia Sourcing",
    description: "The page you are looking for is not in our sitemap. Head to a canonical entry point.",
    path: "/",
    noindex: true,
  });

  return (
    <main
      data-testid="not-found-page"
      style={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "6rem 1.5rem 4rem",
      }}
    >
      <div style={{ maxWidth: "620px", textAlign: "center" }}>
        <p
          className="mono"
          style={{
            color: "var(--brass-deep)",
            fontSize: ".72rem",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            margin: "0 0 1rem",
          }}
        >
          404 · not found
        </p>
        <h1
          className="display"
          style={{
            fontSize: "clamp(2rem, 5vw, 3.2rem)",
            letterSpacing: "-.02em",
            lineHeight: 1.1,
            margin: "0 0 1.2rem",
          }}
        >
          This page is not in our sitemap.
        </h1>
        <p
          style={{
            color: "var(--ink-soft)",
            fontSize: "1.05rem",
            lineHeight: 1.7,
            margin: "0 0 2rem",
            maxWidth: "60ch",
            marginInline: "auto",
          }}
        >
          The URL you followed may be outdated or mistyped. Head back to a canonical entry point and rebuild
          from there.
        </p>
        <nav
          aria-label="Recovery"
          style={{ display: "flex", flexWrap: "wrap", gap: ".8rem", justifyContent: "center" }}
        >
          <Link to="/" className="btn btn-ghost" data-testid="not-found-home">
            Home
          </Link>
          <Link to="/services" className="btn btn-ghost" data-testid="not-found-services">
            Services
          </Link>
          <Link to="/industries" className="btn btn-ghost" data-testid="not-found-industries">
            Industries
          </Link>
          <Link to="/clusters" className="btn btn-ghost" data-testid="not-found-clusters">
            Clusters
          </Link>
          <Link to="/contact" className="btn btn-ghost" data-testid="not-found-contact">
            Contact
          </Link>
        </nav>
      </div>
    </main>
  );
};

export default NotFound;
